import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import {
  Search, Loader2, Phone, Mail, Globe, MapPin, Clock, Trash2, ChevronDown,
  BadgeCheck, Send, Building2,
} from "lucide-react";
import { fetchPros, updatePro, deletePro } from "@/lib/api";

// Pros tab of the admin: applications from the /pros page, with a status
// pipeline, notes, and a coverage view of which pests and counties have an
// active buyer. Nothing here changes how leads are routed.

const STATUSES = ["new", "contacted", "active", "declined"];
const STATUS_STYLES = {
  new: "bg-duckgreen/15 text-forest border-duckgreen/30",
  contacted: "bg-terracotta/15 text-terracotta border-terracotta/30",
  active: "bg-teal/15 text-teal border-teal/30",
  declined: "bg-forest/10 text-forest-950/50 border-forest/15",
};

// Same option lists as the application form on /pros.
const PESTS = ["Mosquitoes", "Ticks", "Wasps/Hornets", "Ants", "Fleas", "Rodents", "General Pest"];
const COUNTIES = ["Fairfield", "Hartford", "Litchfield", "Middlesex", "New Haven", "New London", "Tolland", "Windham"];

const split = (s) => (s ? String(s).split(",").map((x) => x.trim()).filter(Boolean) : []);

const timeAgo = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const siteHref = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

function Tile({ label, value, tone = "default" }) {
  const tones = {
    default: "bg-cream border-forest/10 text-forest-950",
    green: "bg-duckgreen/10 border-duckgreen/25 text-forest",
    teal: "bg-teal/10 border-teal/25 text-teal",
  };
  return (
    <div className={`rounded-2xl border px-4 py-3.5 ${tones[tone]}`}>
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="mt-1.5 text-[11px] uppercase tracking-[0.15em] opacity-70 font-semibold">{label}</div>
    </div>
  );
}

// Which pests and counties have an active buyer, and how many have applied.
// Built for mapping out lead distribution: a 0 in "active" is a gap.
function Coverage({ records }) {
  const count = (field, value, status) =>
    records.filter((r) => (!status || r.status === status) && split(r.pro?.[field]).includes(value)).length;

  const Grid = ({ title, field, values }) => (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-forest-950/45 font-semibold mb-2">{title}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {values.map((v) => {
          const active = count(field, v, "active");
          const applied = count(field, v);
          return (
            <div
              key={v}
              className={`rounded-xl border px-3 py-2.5 ${active ? "bg-teal/10 border-teal/25" : "bg-white border-forest/12"}`}
            >
              <div className="text-sm font-semibold text-forest-950 truncate">{v}</div>
              <div className="mt-0.5 text-xs text-forest-950/60">
                <span className={`font-bold ${active ? "text-teal" : "text-forest-950/40"}`}>{active} active</span>
                {" · "}
                {applied} applied
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="rounded-2xl border border-forest/12 bg-cream p-4 sm:p-5 space-y-4" data-testid="admin-pros-coverage">
      <div>
        <div className="font-serif text-lg font-bold text-forest-950">Coverage</div>
        <p className="text-xs text-forest-950/55 mt-0.5">Pros marked active, by what they treat and where they work.</p>
      </div>
      <Grid title="Pests" field="pests" values={PESTS} />
      <Grid title="Counties" field="counties" values={COUNTIES} />
    </div>
  );
}

function Chips({ items, tone = "plain" }) {
  if (!items.length) return <span className="text-forest-950/40">—</span>;
  const cls = tone === "pest" ? "bg-terracotta/10 text-forest-950 border-terracotta/25" : "bg-white text-forest-950/75 border-forest/12";
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((x) => (
        <span key={x} className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${cls}`}>{x}</span>
      ))}
    </div>
  );
}

function ProCard({ record, token, onChanged }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(record.notes || "");
  const [saving, setSaving] = useState(false);
  const d = record.pro || {};
  const company = d.company || "No company name";

  const save = async (changes) => {
    setSaving(true);
    try {
      await updatePro(token, record.key, changes);
      toast.success("Saved");
      onChanged();
    } catch {
      toast.error("Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the application from ${company}? This can't be undone.`)) return;
    try {
      await deletePro(token, record.key);
      toast.success("Deleted");
      onChanged();
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  return (
    <div className="rounded-2xl border border-forest/12 bg-cream overflow-hidden" data-testid="admin-pro-card">
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-serif text-lg font-bold text-forest-950 truncate">{company}</span>
              <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${STATUS_STYLES[record.status] || STATUS_STYLES.new}`}>
                {record.status}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-forest-950/65">
              {d.contact_name && <span className="font-semibold text-forest">{d.contact_name}</span>}
              <span className="flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" />{d.license_number || "No license #"}</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{timeAgo(record.received_at)}</span>
            </div>
          </div>
          <button
            onClick={() => setOpen(!open)}
            className="shrink-0 p-2 -m-1 rounded-lg hover:bg-pill/60 transition-colors"
            aria-label={open ? "Collapse" : "Expand"}
            data-testid="admin-pro-toggle"
          >
            <ChevronDown className={`w-5 h-5 text-forest-950/50 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>

        <div className="mt-3"><Chips items={split(d.pests)} tone="pest" /></div>
        <div className="mt-2 flex items-start gap-1.5 text-sm text-forest-950/70">
          <MapPin className="w-3.5 h-3.5 mt-[3px] shrink-0" />
          <span>{split(d.counties).join(", ") || "No counties given"}</span>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {d.phone && (
            <a href={`tel:${d.phone}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-forest-950 text-cream text-sm font-semibold active:scale-95 transition-transform">
              <Phone className="w-3.5 h-3.5" /> {d.phone}
            </a>
          )}
          {d.email && (
            <a href={`mailto:${d.email}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-forest/20 text-forest-950 text-sm font-semibold active:scale-95 transition-transform">
              <Mail className="w-3.5 h-3.5" /> Email
            </a>
          )}
          {d.website && (
            <a href={siteHref(d.website)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-forest/20 text-forest-950 text-sm font-semibold active:scale-95 transition-transform">
              <Globe className="w-3.5 h-3.5" /> Website
            </a>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-forest/10 bg-sand/50 p-4 sm:p-5 space-y-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            {[
              ["Contact", d.contact_name], ["CT DEEP license #", d.license_number],
              ["Wants leads by", d.delivery || "Not specified"], ["Email", d.email],
              ["Landing page", d.page], ["Source", d.utm_source || "direct"],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-[11px] uppercase tracking-wider text-forest-950/45 font-semibold">{k}</dt>
                <dd className="text-forest-950/80 break-words">{v || "—"}</dd>
              </div>
            ))}
          </dl>

          {d.notes && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-forest-950/45 font-semibold">What they said</div>
              <p className="mt-1 text-sm text-forest-950/80 leading-relaxed">{d.notes}</p>
            </div>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-wider text-forest-950/45 font-semibold mb-2">Status</div>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => save({ status: s })}
                  disabled={saving}
                  className={`px-3.5 py-2 rounded-full text-sm font-semibold border transition-all active:scale-95 ${
                    record.status === s ? STATUS_STYLES[s] : "bg-white border-forest/15 text-forest-950/60"
                  }`}
                  data-testid={`admin-pro-status-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-forest-950/45 font-semibold mb-2">Your notes</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="License checked? Pricing agreed? Which pests and towns they get…"
              className="w-full px-3.5 py-3 rounded-xl border border-forest/15 bg-white text-sm text-forest-950 focus:outline-none focus:ring-2 focus:ring-duckgreen"
            />
            <div className="mt-2 flex items-center justify-between gap-3">
              <button
                onClick={() => save({ notes })}
                disabled={saving || notes === (record.notes || "")}
                className="px-5 py-2.5 rounded-full bg-forest-950 text-cream text-sm font-semibold disabled:opacity-40 active:scale-95 transition-transform"
              >
                Save notes
              </button>
              <button
                onClick={remove}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-full text-sm font-semibold text-terracotta hover:bg-terracotta/10 transition-colors"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPros({ token, refreshKey = 0, onUnauthorized }) {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // A quiet reload (after a save) keeps the list on screen, so an open card
  // stays open instead of collapsing behind a spinner.
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await fetchPros(token);
      setRecords(res.pros || []);
    } catch (err) {
      if (String(err.message) === "unauthorized") onUnauthorized?.();
      else toast.error("Couldn't load pro applications.");
    } finally {
      setLoading(false);
    }
  }, [token, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return records.filter((r) => {
      if (statusFilter !== "All" && r.status !== statusFilter) return false;
      if (!q) return true;
      const d = r.pro || {};
      return [d.company, d.contact_name, d.email, d.phone, d.license_number, d.pests, d.counties, d.notes]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [records, query, statusFilter]);

  const stats = useMemo(() => ({
    total: records.length,
    newCount: records.filter((r) => r.status === "new").length,
    contacted: records.filter((r) => r.status === "contacted").length,
    active: records.filter((r) => r.status === "active").length,
  }), [records]);

  return (
    <div className="space-y-5" data-testid="admin-pros">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Tile label="Applications" value={stats.total} />
        <Tile label="New" value={stats.newCount} tone="green" />
        <Tile label="Contacted" value={stats.contacted} />
        <Tile label="Active" value={stats.active} tone="teal" />
      </div>

      {!loading && records.length > 0 && <Coverage records={records} />}

      <div className="space-y-2.5">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-forest-950/35" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search company, name, license, county…"
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-forest/12 bg-cream text-base text-forest-950 focus:outline-none focus:ring-2 focus:ring-duckgreen"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {["All", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`shrink-0 px-3.5 py-2 rounded-full text-sm font-semibold border transition-all ${
                statusFilter === s ? "bg-teal text-white border-teal" : "bg-cream text-forest-950/65 border-forest/15"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-forest/40" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center">
          <Building2 className="w-10 h-10 mx-auto text-forest/20" />
          <p className="mt-4 text-forest-950/55">
            {records.length === 0 ? "No pro applications yet." : "Nothing matches those filters."}
          </p>
          {records.length === 0 && (
            <p className="mt-1.5 text-sm text-forest-950/45 flex items-center justify-center gap-1.5">
              <Send className="w-3.5 h-3.5" /> They'll show up here when someone applies at /pros.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <ProCard key={r.key} record={r} token={token} onChanged={() => load(true)} />
          ))}
        </div>
      )}
    </div>
  );
}
